import { Actor, CollisionType, Color, Engine, vec, Shape, Sprite } from "excalibur";
import { WEAPONS, VISUALS } from "../config";
import { Virus } from "./Virus";
import { ProjectileCollision } from "../systems/CollisionGroups";
import { Resources } from "../resources";

/**
 * Cached antibody sprite (shared across all instances)
 * Antibodies fire frequently, so this avoids creating new sprites constantly
 */
let cachedAntibodySprite: Sprite | null = null;

function getCachedAntibodySprite(): Sprite {
    if (!cachedAntibodySprite) {
        cachedAntibodySprite = Resources.Antibody.toSprite();

        // Scale to match the antibody radius
        const targetSize = WEAPONS.ANTIBODY.RADIUS * 2;
        const scale = targetSize / Math.max(cachedAntibodySprite.width, cachedAntibodySprite.height);
        cachedAntibodySprite.scale = vec(scale, scale);
    }
    return cachedAntibodySprite;
}

/**
 * Antibody - The Player's Auto-Targeting Projectile
 * 
 * IgG antibodies that automatically seek and destroy viruses.
 * Visual: Blue Y-shaped antibody sprite
 * 
 * Uses Passive collision to trigger damage events without physically pushing enemies.
 */
export class Antibody extends Actor {
    private target: Virus | null = null;
    private damage: number = WEAPONS.ANTIBODY.DAMAGE;
    private speed: number = WEAPONS.ANTIBODY.SPEED;

    constructor(x: number, y: number, target: Virus) {
        super({
            name: 'Antibody',
            pos: vec(x, y),
            radius: WEAPONS.ANTIBODY.RADIUS,
            color: Color.fromHex(VISUALS.COLORS.ANTIBODY),
            collisionType: CollisionType.Passive, // Triggers events only, no physics push
            collisionGroup: ProjectileCollision,  // Only collides with enemies
        });
        this.target = target;
    }

    override onInitialize(engine: Engine): void {
        // Set up circular collider and restore collision group manually
        const collider = Shape.Circle(WEAPONS.ANTIBODY.RADIUS) as any;
        collider.group = ProjectileCollision;
        this.collider.set(collider);

        // Use cached antibody sprite (avoids creating new sprites for each projectile)
        const sprite = getCachedAntibodySprite();
        this.graphics.use(sprite);

        // Calculate initial velocity toward target
        if (this.target && this.target.isPoolActive()) {
            const direction = this.target.pos.sub(this.pos).normalize();
            this.vel = direction.scale(this.speed);

            // Rotate sprite to face direction of travel
            this.rotation = Math.atan2(direction.y, direction.x) + Math.PI / 2;
        }

        // Auto-destroy after traveling too far
        this.actions.delay(3000).callMethod(() => {
            this.kill();
        });
    }

    override onPreUpdate(_engine: Engine, _delta: number): void {
        // Projectile continues in straight line
    }

    /**
     * Get the damage this antibody deals
     */
    public getDamage(): number {
        return this.damage;
    }
}

